// Shared types for the RequestAccessModal feature.
//
// The modal flattens the backend's nested (Catalog → CatalogItem → SelectableResource)
// shape into a single grid of "request items" — each row in the grid is one
// (catalogId, catalogItemId, selectableResourceId) tuple.

import type {
  MFAccessCatalog,
  MFCatalogItem,
  MFCatalogItemSelectableResource,
} from "state/members/access_v2/catalogs";

// Stands in for a recipient id: whoever the request is for.
export const EVERY_RECIPIENT = "*";

export interface FlatRequestItem {
  /**
   * Stable composite key: `${catalogId}__${catalogItemId}__${selectableResourceId}`.
   * Includes catalogItemId because the same selectable resource can be
   * attached to multiple catalog items within the same catalog — without
   * the middle segment those rows collide on React keys.
   */
  id: string;
  catalogId: string;
  catalogName: string;
  catalogItemId: string;
  /** The parent app/catalog item — its name and image power the card chrome. */
  catalogItem: MFCatalogItem;
  /** The leaf — what the user actually requests access to. */
  resource: MFCatalogItemSelectableResource;
}

/**
 * What a row actually grants. The browse grid lists one card per
 * (catalog, catalog item, resource) tuple, so a resource published in several
 * catalogs — or under several catalog items — shows up as several cards. They
 * all resolve to this one id, which is what the backend grants against.
 */
export const resourceKey = (item: FlatRequestItem): string =>
  item.resource.selectableResource.id;

/**
 * Keep the first row per underlying selectable resource. Requesting the same
 * resource from two catalogs is not two requests, it's the same grant asked for
 * twice, so the extra rows are dropped rather than fanned out into duplicate
 * (and mutually-conflicting) access requests.
 */
export const dedupeByResource = (
  items: FlatRequestItem[],
): FlatRequestItem[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = resourceKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Browse-row id → the selectable resource it grants. */
export const resourceIdIndex = (
  items: FlatRequestItem[],
): Map<string, string> => {
  const index = new Map<string, string>();
  for (const item of items) index.set(item.id, resourceKey(item));
  return index;
};

/**
 * The basket ids that already cover the resource `id` grants — `id` itself if
 * it's in the basket, plus any sibling row for the same resource published in
 * another catalog. Empty means nothing in the basket covers it yet.
 */
export const heldIdsForResource = (
  id: string,
  basket: string[],
  resourceById: Map<string, string>,
): string[] => {
  const resource = resourceById.get(id);
  return basket.filter(
    (held) =>
      held === id ||
      (resource !== undefined && resourceById.get(held) === resource),
  );
};

/**
 * Add, remove, or reattribute a browse row, keyed on the resource rather than
 * the row. Clicking the row that holds the resource removes it; clicking a
 * SIBLING row (same resource published in another catalog, shown as the
 * "covered" card) transfers the selection to the clicked row instead of
 * removing — the request files under the catalog the user last chose.
 */
export const toggleResourceSelection = (
  basket: string[],
  id: string,
  resourceById: Map<string, string>,
): string[] => {
  const held = heldIdsForResource(id, basket, resourceById);
  if (held.length === 0) return [...basket, id];
  if (held.includes(id)) return basket.filter((x) => !held.includes(x));
  return [...basket.filter((x) => !held.includes(x)), id];
};

export const flattenCatalogs = (
  catalogs: MFAccessCatalog[],
): FlatRequestItem[] => {
  const out: FlatRequestItem[] = [];
  for (const cat of catalogs) {
    for (const item of cat.items ?? []) {
      for (const res of item.catalogItemSelectableResources ?? []) {
        out.push({
          id: `${cat.id}__${item.id}__${res.selectableResource.id}`,
          catalogId: cat.id,
          catalogName: cat.name,
          catalogItemId: item.id,
          catalogItem: item,
          resource: res,
        });
      }
    }
  }
  return out;
};
